import React, { useState, useEffect, useRef } from 'react';
import { Header, Loader, Button } from 'semantic-ui-react';
import { getDashboardChart } from '~/api/admin';
import { DeploymentTypeDisplay } from '~/constants/deployment-type';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  ScatterController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { DEPLOYMENT_TYPE_COLORS } from './chartColors';

ChartJS.register(
  LinearScale,
  PointElement,
  ScatterController,
  Title,
  Tooltip,
  Legend
);

interface DataPoint {
  x: number;
  y: number;
  deploymentType: string;
  projectName: string;
}

interface ChartData {
  dataPoints: DataPoint[];
}

interface BudgetVsSizeChartProps {
  interval?: string;
  onIntervalChange?: (interval: string) => void;
  states?: string[];
}

type YAxisMode = 'total' | 'perWdc';

const BudgetVsSizeChart: React.FC<BudgetVsSizeChartProps> = ({
  interval: intervalProp = 'all',
  states = [],
}) => {
  const [data, setData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [yAxisMode, setYAxisMode] = useState<YAxisMode>('perWdc');
  const interval = intervalProp;
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<ChartJS | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params: Record<string, string> = { interval };
        if (states.length) params.states = states.join(',');
        const result = await getDashboardChart('budget-vs-size', params);
        setData(result || null);
      } catch (error) {
        console.error('Error fetching budget vs size chart data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [interval, states]);

  useEffect(() => {
    if (!loading && data?.dataPoints?.length && chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        const pointsByType: Record<string, DataPoint[]> = {};
        for (const point of data.dataPoints) {
          const type = point.deploymentType || 'CI';
          if (!pointsByType[type]) pointsByType[type] = [];
          pointsByType[type].push(point);
        }

        const datasets = Object.entries(pointsByType).map(([type, points]) => {
          const dataPoints = points.map((p) => {
            const sizeMw = p.x;
            const totalBudget = p.y;
            const budgetPerWdc =
              sizeMw > 0 ? totalBudget / (sizeMw * 1_000_000) : 0;
            const yValue = yAxisMode === 'perWdc' ? budgetPerWdc : totalBudget;

            return {
              x: sizeMw,
              y: yValue,
              projectName: p.projectName,
              totalBudget,
              budgetPerWdc,
            };
          });

          return {
            label:
              DeploymentTypeDisplay[type as keyof typeof DeploymentTypeDisplay] ||
              type,
            data: dataPoints,
            backgroundColor: DEPLOYMENT_TYPE_COLORS[type] || '#999',
            pointRadius: 6,
            pointHoverRadius: 8,
          };
        });

        chartInstance.current = new ChartJS(ctx, {
          type: 'scatter',
          data: { datasets },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
              },
              title: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const raw = context.raw as {
                      x: number;
                      y: number;
                      projectName?: string;
                      totalBudget?: number;
                      budgetPerWdc?: number;
                    };
                    const sizeMw = raw.x;
                    const yValue = raw.y;
                    const totalBudget = raw.totalBudget;
                    const budgetPerWdc = raw.budgetPerWdc;

                    const lines: string[] = [
                      raw.projectName || 'Unknown',
                      `Size: ${sizeMw.toLocaleString()} MWdc`,
                    ];

                    if (typeof totalBudget === 'number') {
                      lines.push(`Total budget: $${totalBudget.toLocaleString()}`);
                    }

                    if (typeof budgetPerWdc === 'number') {
                      if (yAxisMode === 'perWdc') {
                        lines.push(`Budget: $${budgetPerWdc.toFixed(2)}/Wdc`);
                      } else {
                        lines.push(
                          `Budget: $${budgetPerWdc.toFixed(2)}/Wdc (per Wdc)`
                        );
                      }
                    }

                    if (yAxisMode === 'total' && typeof yValue === 'number') {
                      lines.push(`Y-axis: $${yValue.toLocaleString()}`);
                    } else if (yAxisMode === 'perWdc') {
                      lines.push(`Y-axis: $${yValue.toFixed(2)}/Wdc`);
                    }

                    return lines;
                  },
                },
              },
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Project Size (MWdc)',
                },
                beginAtZero: true,
              },
              y: {
                title: {
                  display: true,
                  text: yAxisMode === 'perWdc' ? 'Budget ($/Wdc)' : 'Budget ($)',
                },
                beginAtZero: true,
                ticks: {
                  callback: (value) => {
                    const num = Number(value);
                    if (yAxisMode === 'perWdc') {
                      return `$${num.toFixed(2)}`;
                    }
                    if (num >= 1_000_000) {
                      return `$${(num / 1_000_000).toFixed(1)}M`;
                    }
                    if (num >= 1_000) {
                      return `$${(num / 1_000).toFixed(0)}K`;
                    }
                    return `$${num.toFixed(0)}`;
                  },
                },
              },
            },
          },
        });
      }

      return () => {
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }
      };
    }
  }, [data, loading, yAxisMode]);

  if (loading) {
    return (
      <div className="card">
        <Header as="h2">Project Budget vs PV Size</Header>
        <Loader active inline="centered" />
      </div>
    );
  }

  return (
    <div className="card">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <Header as="h2" style={{ margin: 0 }}>
          Project Budget vs PV Size
        </Header>
        <Button.Group size="small">
          <Button
            active={yAxisMode === 'perWdc'}
            onClick={() => setYAxisMode('perWdc')}
          >
            $/Wdc
          </Button>
          <Button
            active={yAxisMode === 'total'}
            onClick={() => setYAxisMode('total')}
          >
            Total $
          </Button>
        </Button.Group>
      </div>
      {data?.dataPoints?.length ? (
        <div style={{ height: '400px', width: '100%' }}>
          <canvas ref={chartRef} />
        </div>
      ) : (
        <p>No data available</p>
      )}
    </div>
  );
};

export default BudgetVsSizeChart;
